import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { MsalProvider } from '@azure/msal-react'
import { PublicClientApplication } from '@azure/msal-browser'
import { msalConfig } from './config/authConfig'
import Layout from './components/Layout'
import DataSourceLayout from './components/DataSourceLayout'
import { ToastProvider } from './contexts/ToastContext'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { LanguageProvider } from './contexts/LanguageContext'
import { DataAdmissionProvider } from './contexts/DataAdmissionContext'
import ToastContainer from './components/ToastContainer'
import AuthLoading from './components/auth/AuthLoading'
import { useTranslate } from './hooks/useTranslate'
import DataSource from './pages/DataSource'
import EntryPage from './pages/EntryPage'
import { NewReportPage } from './pages/NewReportPage'
import { SharedReportPage } from './pages/SharedReportPage'
// Legacy snapshot-based page kept temporarily for reference; backend page is the active route
// import HistoricalReportsPage from './pages/HistoricalReportsPage.tsx';
import { BackendHistoricalReportsPage } from './pages/BackendHistoricalReportsPage';
import TrainingPage from './pages/TrainingPage';
import AdminPanel from './components/AdminPanel';
import { useState, useEffect } from 'react'

// Create the MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Main App Content (inside AuthProvider)
const AppContent = () => {
    const { isAuthenticated, isLoading, error, login } = useAuth();
    const { t } = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();
    const [autoLoginTriggered, setAutoLoginTriggered] = useState(false);

    // Auto-trigger login for shared-report links (must be in useEffect, not render)
    useEffect(() => {
        if (!isAuthenticated && !isLoading && !error && !autoLoginTriggered) {
            if (location.pathname.startsWith('/shared-report')) {
                sessionStorage.setItem('dd_redirect_after_login', location.pathname + location.search);
                setAutoLoginTriggered(true);
                login();
            }
        }
    }, [isAuthenticated, isLoading, error, autoLoginTriggered, location.pathname, location.search, login]);

    // Handle logout redirect - when user becomes unauthenticated while on a protected route
    useEffect(() => {
        if (!isAuthenticated && !isLoading && !error && !autoLoginTriggered) {
            if (location.pathname !== '/' && location.pathname !== '/home' && !location.pathname.startsWith('/shared-report')) {
                navigate('/', { replace: true });
            }
        }
    }, [isAuthenticated, isLoading, error, autoLoginTriggered, location.pathname, navigate]);

    // Show loading during authentication process or auto-login redirect
    if (isLoading || autoLoginTriggered) {
        return <AuthLoading message={autoLoginTriggered ? t('Redirecting to login...') : t('Authenticating with Azure AD...')} />;
    }

    // Show error state with retry option
    if (error && !isAuthenticated) {
        return (
            <div className="min-h-screen bg-black text-white flex items-center justify-center">
                <div className="text-center space-y-6 max-w-md">
                    <h1 className="text-3xl font-bold text-white">DATA DOCTOR</h1>
                    <div className="bg-red-900/20 border border-red-500 rounded-lg p-6">
                        <p className="text-red-400 mb-4">{t('Authentication Error')}</p>
                        <p className="text-gray-300 text-sm mb-4">{error}</p>
                        <button
                            onClick={login}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
                        >
                            {t('Retry Login')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Show authenticated app
    if (isAuthenticated) {
        return (
            <div className="min-h-screen bg-black text-white">
                <Routes>
                    {/* Default route — check for saved redirect (shared-report link) first */}
                    <Route path="/" element={<AuthenticatedHomeRedirect />} />

                    {/* Main application routes */}
                    <Route path="/datasources" element={<DataSourceLayout><DataSource /></DataSourceLayout>} />
                    <Route path="/report" element={<NewReportPage />} />
                    <Route path="/shared-report" element={<SharedReportPage />} />
                    {/** Use new backend historical reports page */}
                    <Route path="/reports" element={<DataSourceLayout><BackendHistoricalReportsPage /></DataSourceLayout>} />
                    <Route path="/training" element={<DataSourceLayout><TrainingPage /></DataSourceLayout>} />
                    <Route path="/admin" element={<DataSourceLayout><AdminPanel /></DataSourceLayout>} />

                    {/* Catch-all redirect */}
                    <Route path="*" element={<Navigate to="/datasources" replace />} />
                </Routes>
                <ToastContainer />
            </div>
        );
    }

    // Show EntryPage for unauthenticated users
    return (
        <div className="min-h-screen bg-black text-white">
            <Routes>
                {/* Public routes - EntryPage for unauthenticated users */}
                <Route path="/" element={<Layout><EntryPage /></Layout>} />
                <Route path="/home" element={<Layout><EntryPage /></Layout>} />

                {/* Catch-all redirect to home for unauthenticated users */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <ToastContainer />
        </div>
    );
};

/**
 * Handles the authenticated "/" route.
 * Checks sessionStorage for a saved redirect URL (e.g., shared-report link
 * that triggered login) and navigates there. Otherwise goes to /datasources.
 * This runs as a route component so it fires BEFORE any parent useEffect,
 * avoiding the race condition with <Navigate>.
 */
function AuthenticatedHomeRedirect() {
    const navigate = useNavigate();
    useEffect(() => {
        const savedRedirect = sessionStorage.getItem('dd_redirect_after_login');
        if (savedRedirect) {
            sessionStorage.removeItem('dd_redirect_after_login');
            navigate(savedRedirect, { replace: true });
        } else {
            navigate('/datasources', { replace: true });
        }
    }, [navigate]);
    return null;
}

function App() {
    return (
        <MsalProvider instance={msalInstance}>
            <AuthProvider>
                <ThemeProvider>
                    <LanguageProvider>
                        <ToastProvider>
                            <DataAdmissionProvider>
                                <AppContent />
                            </DataAdmissionProvider>
                        </ToastProvider>
                    </LanguageProvider>
                </ThemeProvider>
            </AuthProvider>
        </MsalProvider>
    );
}

export default App;